/**
 * Shell-owned assistant session: floating support chat, default agent only.
 * Not the full agent app — just create + stream one conversation.
 */

import { computed, ref, shallowRef } from 'vue';
import { useNxSocket } from '@blpde/connect';
import type { AgentRecordContext } from '@blpde/connect';
import {
  createConversation,
  listAgents,
  pickDefaultAgent,
  restChat,
  type ShellAgent,
  type ShellConversation,
  type ShellRecordContext,
} from './agentApi';

export interface ShellAssistantMessage {
  id: string;
  role: 'user' | 'agent' | 'system';
  content: string;
  pending?: boolean;
  error?: string;
}

interface ChatFrameIn {
  type: string;
  requestId?: string;
  text?: string;
  error?: string;
  stopReason?: string;
}

const open = ref(false);
const booting = ref(false);
const sending = ref(false);
const error = ref<string | null>(null);
const agent = shallowRef<ShellAgent | null>(null);
const conversation = shallowRef<ShellConversation | null>(null);
const context = shallowRef<AgentRecordContext | null>(null);
const messages = ref<ShellAssistantMessage[]>([]);
const draft = ref('');

let inFlightRequestId: string | null = null;
let channelWired = false;
let frameHandler: ((frame: ChatFrameIn) => void) | null = null;

function toApiContext(ctx: AgentRecordContext | null | undefined): ShellRecordContext | null {
  if (!ctx?.moduleKey?.trim() || !ctx.resourceType?.trim() || !ctx.resourceId?.trim()) {
    return null;
  }
  return {
    module_key: ctx.moduleKey.trim(),
    resource_type: ctx.resourceType.trim(),
    resource_id: ctx.resourceId.trim(),
    ...(ctx.label?.trim() ? { label: ctx.label.trim() } : {}),
  };
}

function newMsgId(): string {
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function ensureChannel(socket: ReturnType<typeof useNxSocket> | null | undefined) {
  if (!socket || channelWired) return;
  channelWired = true;
  const ch = socket.channel('agent');
  ch.on('data', (raw: unknown) => {
    const frame = raw as ChatFrameIn;
    frameHandler?.(frame);
  });
}

function handleFrame(frame: ChatFrameIn) {
  if (!inFlightRequestId || frame.requestId !== inFlightRequestId) return;

  switch (frame.type) {
    case 'text_delta': {
      const last = messages.value.at(-1);
      if (last?.role === 'agent' && last.pending) {
        last.content += frame.text ?? '';
        messages.value = [...messages.value];
      }
      break;
    }
    case 'agent_done': {
      const last = messages.value.at(-1);
      if (last?.role === 'agent') {
        last.pending = false;
        messages.value = [...messages.value];
      }
      sending.value = false;
      inFlightRequestId = null;
      break;
    }
    case 'agent_error': {
      const last = messages.value.at(-1);
      if (last?.role === 'agent') {
        last.pending = false;
        last.error = frame.error || 'error';
        messages.value = [...messages.value];
      }
      sending.value = false;
      inFlightRequestId = null;
      break;
    }
    default:
      break;
  }
}

export function useShellAssistant() {
  const socket = useNxSocket();
  ensureChannel(socket);
  frameHandler = handleFrame;

  const contextLine = computed(() => {
    const c = context.value;
    if (!c) return '';
    if (c.label?.trim()) return c.label.trim();
    return `${c.moduleKey}/${c.resourceType}#${c.resourceId}`;
  });

  const stickKey = computed(() => {
    const last = messages.value.at(-1);
    return `${messages.value.length}:${last?.content?.length ?? 0}:${last?.pending ? 1 : 0}`;
  });

  async function openAssistant(recordCtx?: AgentRecordContext | null) {
    open.value = true;
    error.value = null;
    booting.value = true;
    context.value = recordCtx?.moduleKey ? { ...recordCtx } : null;
    messages.value = [];
    conversation.value = null;
    draft.value = '';
    sending.value = false;
    inFlightRequestId = null;

    try {
      const agents = await listAgents();
      const picked = pickDefaultAgent(agents);
      if (!picked) {
        error.value = 'no-agent';
        booting.value = false;
        return;
      }
      agent.value = picked;

      const apiCtx = toApiContext(recordCtx);
      const title =
        apiCtx?.label ||
        (apiCtx ? `${apiCtx.resource_type} ${apiCtx.resource_id}` : '');
      const conv = await createConversation(picked.id, {
        title: title || undefined,
        context: apiCtx,
      });
      conversation.value = conv;
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'error';
    } finally {
      booting.value = false;
    }
  }

  function closeAssistant() {
    // A reply still streaming has no panel to land in: tell the agent to stop.
    cancel();
    open.value = false;
    sending.value = false;
    inFlightRequestId = null;
  }

  async function send(content: string) {
    const text = content.trim();
    const conv = conversation.value;
    const a = agent.value;
    if (!text || !conv || !a || sending.value || booting.value) return;

    draft.value = '';
    messages.value = [
      ...messages.value,
      { id: newMsgId(), role: 'user', content: text },
      { id: newMsgId(), role: 'agent', content: '', pending: true },
    ];
    sending.value = true;
    error.value = null;

    const requestId = `shell-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    inFlightRequestId = requestId;

    const canStream = !!socket;
    if (canStream) {
      try {
        const uiContext: Record<string, unknown> = {
          source: 'connect.shell.assistant',
          ts: Date.now(),
        };
        if (context.value) {
          uiContext.record = {
            module_key: context.value.moduleKey,
            resource_type: context.value.resourceType,
            resource_id: context.value.resourceId,
            ...(context.value.label ? { label: context.value.label } : {}),
          };
        }
        socket.channel('agent').send({
          type: 'chat',
          requestId,
          conversationId: conv.id,
          agentId: a.id,
          message: text,
          uiContext,
        });
        return;
      } catch {
        // fall through to REST
      }
    }

    try {
      const res = await restChat(a.id, conv.id, text);
      const last = messages.value.at(-1);
      if (last?.role === 'agent') {
        last.content = res.content;
        last.pending = false;
        messages.value = [...messages.value];
      }
    } catch (e: unknown) {
      const last = messages.value.at(-1);
      if (last?.role === 'agent') {
        last.pending = false;
        last.error = e instanceof Error ? e.message : 'error';
        messages.value = [...messages.value];
      }
      error.value = e instanceof Error ? e.message : 'error';
    } finally {
      sending.value = false;
      inFlightRequestId = null;
    }
  }

  function cancel() {
    if (!socket || !inFlightRequestId) return;
    socket.channel('agent').send({
      type: 'cancel',
      requestId: inFlightRequestId,
    });
    const last = messages.value.at(-1);
    if (last?.role === 'agent' && last.pending) {
      last.pending = false;
      messages.value = [...messages.value];
    }
    sending.value = false;
    inFlightRequestId = null;
  }

  return {
    open,
    booting,
    sending,
    error,
    agent,
    conversation,
    context,
    contextLine,
    messages,
    draft,
    stickKey,
    openAssistant,
    closeAssistant,
    send,
    cancel,
  };
}
